import React, { useState } from 'react';
import { db, collection, addDoc, deleteDoc, doc, Timestamp } from '../firebase';
import { QACategory, QAQuestion } from '../types';
import { Sliders, Plus, Loader2, Award, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface QAManagerProps {
  categories: QACategory[];
  questions: QAQuestion[];
  isAdminOrManager: boolean;
  onSeedDefaults: () => Promise<void>;
  isSeeding: boolean;
}

export const QAManager: React.FC<QAManagerProps> = ({
  categories,
  questions,
  isAdminOrManager,
  onSeedDefaults,
  isSeeding
}) => {
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  
  // Category Form State
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  // Question Form State
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionMarks, setNewQuestionMarks] = useState<number>(10);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);

  // Add Category Handler
  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    setIsAddingCategory(true);
    try {
      await addDoc(collection(db, 'qa_categories'), {
        name: newCategoryName.trim(),
        description: newCategoryDesc.trim(),
        createdAt: Timestamp.now()
      });
      toast.success('Questionnaire Category registered successfully!');
      setNewCategoryName('');
      setNewCategoryDesc('');
    } catch (err) {
      console.error('Error adding category:', err);
      toast.error('Failed to register category');
    } finally {
      setIsAddingCategory(false);
    }
  };

  // Delete Category Handler
  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category? All questions under this category will also be deleted.')) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'qa_categories', id));
      
      const associatedQuestions = questions.filter(q => q.categoryId === id);
      const deletePromises = associatedQuestions.map(q => deleteDoc(doc(db, 'qa_questions', q.id)));
      await Promise.all(deletePromises);
      
      toast.success('Category and its questions deleted successfully');
      if (activeCategoryId === id) {
        setActiveCategoryId(null);
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Failed to delete category');
    }
  };

  // Add Question Handler
  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeCategoryId) {
      toast.error('Please select a category first');
      return;
    }
    if (!newQuestionText.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (newQuestionMarks <= 0) {
      toast.error('Marks must be greater than 0');
      return;
    }

    const currentCatQuestions = questions.filter(q => q.categoryId === activeCategoryId);
    const currentTotalMarks = currentCatQuestions.reduce((sum, q) => sum + q.maxMarks, 0);

    if (currentTotalMarks + newQuestionMarks > 100) {
      toast.error(`Cannot add question. Total marks in a category cannot exceed 100. (Current: ${currentTotalMarks}, Tried to add: ${newQuestionMarks}, Total: ${currentTotalMarks + newQuestionMarks})`);
      return;
    }

    const category = categories.find(c => c.id === activeCategoryId);
    if (!category) return;

    setIsAddingQuestion(true);
    try {
      await addDoc(collection(db, 'qa_questions'), {
        categoryId: activeCategoryId,
        categoryName: category.name,
        text: newQuestionText.trim(),
        maxMarks: newQuestionMarks,
        createdAt: Timestamp.now()
      });
      toast.success('Question registered successfully!');
      setNewQuestionText('');
      setNewQuestionMarks(10);
    } catch (err) {
      console.error('Error adding question:', err);
      toast.error('Failed to register question');
    } finally {
      setIsAddingQuestion(false);
    }
  };

  // Delete Question Handler
  const handleDeleteQuestion = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'qa_questions', id));
      toast.success('Question deleted successfully');
    } catch (err) {
      console.error('Error deleting question:', err);
      toast.error('Failed to delete question');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Left Panel: Categories List & Create */}
      <div className="space-y-6 lg:col-span-1">
        <div className="bg-white rounded-[2rem] border border-slate-100 p-6 shadow-sm space-y-6">
          <div>
            <h3 className="text-lg font-black text-slate-900">Questionnaire Categories</h3>
            <p className="text-slate-500 text-xs mt-1">Create and manage your performance categories.</p>
          </div>

          {/* Add Category Form */}
          {isAdminOrManager ? (
            <form onSubmit={handleAddCategory} className="space-y-4 pt-4 border-t border-slate-50">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Category Name *</label>
                <input
                  type="text"
                  required
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g. Technical Services"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-sm text-slate-800"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Description</label>
                <textarea
                  rows={2}
                  value={newCategoryDesc}
                  onChange={(e) => setNewCategoryDesc(e.target.value)}
                  placeholder="Describe what this category focuses on..."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium text-xs text-slate-700"
                />
              </div>
              <button
                type="submit"
                disabled={isAddingCategory}
                className="w-full py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isAddingCategory ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Register Category</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-500">
              Only managers or admins can add or configure questionnaire categories.
            </div>
          )}

          {/* Seed Button */}
          {categories.length === 0 && (
            <div className="p-4 bg-orange-50/50 rounded-2xl border border-orange-100 text-center space-y-3">
              <p className="text-xs text-orange-800 font-medium">Want to start with some sample questionnaires instantly?</p>
              <button
                type="button"
                onClick={onSeedDefaults}
                disabled={isSeeding}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs rounded-xl transition-all cursor-pointer inline-flex items-center gap-1.5"
              >
                {isSeeding ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Award className="w-3.5 h-3.5" />
                    <span>Seed Default Questionnaires</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Categories List View */}
          <div className="space-y-3 pt-4 border-t border-slate-50">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Registered Categories ({categories.length})</h4>
            {categories.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No categories registered yet.</p>
            ) : (
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {categories.map((cat) => {
                  const catQuestions = questions.filter(q => q.categoryId === cat.id);
                  const questionCount = catQuestions.length;
                  const totalMarks = catQuestions.reduce((sum, q) => sum + q.maxMarks, 0);
                  const isSelected = activeCategoryId === cat.id;

                  return (
                    <div
                      key={cat.id}
                      onClick={() => setActiveCategoryId(cat.id)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer text-left relative group ${isSelected ? 'bg-blue-50/50 border-blue-200' : 'bg-slate-50 hover:bg-slate-100 border-slate-100'}`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="pr-4">
                          <h5 className="text-xs sm:text-sm font-black text-slate-900 tracking-tight">{cat.name}</h5>
                          {cat.description && (
                            <p className="text-[11px] text-slate-500 font-medium line-clamp-1 mt-0.5">{cat.description}</p>
                          )}
                          <div className="flex gap-2 mt-2">
                            <span className="text-[10px] bg-white text-slate-600 px-2 py-0.5 rounded-md border border-slate-100 font-bold">
                              {questionCount} Qs
                            </span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${totalMarks === 100 ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-orange-50 text-orange-700 border border-orange-100'}`}>
                              {totalMarks}/100 Marks
                            </span>
                          </div>
                        </div>
                        {isAdminOrManager && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(cat.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 absolute top-3 right-3 text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-all"
                            title="Delete category"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel: Managing questions for active category */}
      <div className="lg:col-span-2 space-y-6">
        {activeCategoryId ? (() => {
          const category = categories.find(c => c.id === activeCategoryId);
          if (!category) return null;

          const categoryQuestions = questions.filter(q => q.categoryId === activeCategoryId);
          const totalCategoryMarks = categoryQuestions.reduce((sum, q) => sum + q.maxMarks, 0);

          return (
            <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm space-y-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-50">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Active Category</span>
                  <h3 className="text-xl font-black text-slate-900">{category.name}</h3>
                  {category.description && (
                    <p className="text-slate-500 font-medium text-xs mt-1">{category.description}</p>
                  )}
                </div>
                
                {/* Marks counter widget */}
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 min-w-[180px] flex flex-col justify-center">
                  <div className="flex justify-between items-center text-xs font-bold mb-1.5">
                    <span className="text-slate-500">Marks Allocated:</span>
                    <span className={totalCategoryMarks === 100 ? 'text-emerald-600 font-black' : 'text-orange-600 font-black'}>
                      {totalCategoryMarks} / 100
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${totalCategoryMarks === 100 ? 'bg-emerald-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(totalCategoryMarks, 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Total must not exceed 100 marks.</p>
                </div>
              </div>

              {/* Register New Question Form */}
              {isAdminOrManager ? (
                <form onSubmit={handleAddQuestion} className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100 space-y-4">
                  <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest">Register Question in Category</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3 space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Question Description *</label>
                      <input
                        type="text"
                        required
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        placeholder="e.g. Rate speed in resolving electrical/mechanical faults"
                        className="w-full px-4 py-2.5 bg-white border border-slate-150 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Marks Allocation *</label>
                      <input
                        type="number"
                        required
                        min="1"
                        max={100 - totalCategoryMarks}
                        value={newQuestionMarks}
                        onChange={(e) => setNewQuestionMarks(Number(e.target.value))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-150 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-black text-slate-800 text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={totalCategoryMarks >= 100 || isAddingQuestion}
                      className="px-6 py-2.5 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isAddingQuestion ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Add Question</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-500">
                  Only managers or admins can add or configure questions and marks.
                </div>
              )}

              {/* Question list for this Category */}
              <div className="space-y-4">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest pb-1 border-b border-slate-100">Registered Questions ({categoryQuestions.length})</h4>
                
                {categoryQuestions.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No questions registered in this category yet. Create one above to get started.</p>
                ) : (
                  <div className="space-y-2">
                    {categoryQuestions.map((q, idx) => (
                      <div key={q.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between gap-4 hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-900 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-sm font-bold text-slate-900 leading-relaxed">{q.text}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 bg-white border border-slate-200 text-slate-800 font-black text-xs rounded-lg whitespace-nowrap">
                            {q.maxMarks} Marks
                          </span>
                          {isAdminOrManager && (
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-xl transition-all cursor-pointer"
                              title="Delete question"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })() : (
          <div className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center shadow-sm h-full flex flex-col items-center justify-center space-y-4 min-h-[300px]">
            <Sliders className="w-12 h-12 text-blue-900" />
            <div>
              <h3 className="text-lg font-black text-slate-900">Select a Category</h3>
              <p className="text-slate-500 font-medium text-xs mt-1">Choose an evaluation category from the left sidebar to manage or add questions.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
